/**
 * BuyerAuthContext — Iter 191u
 *
 * Per-vendor buyer session on the storefront. Session token is stored in
 * localStorage keyed by subdomain so a buyer signed in to Vendor A is not
 * signed in to Vendor B.
 *
 * The provider fetches `/api/public/buyers/{sub}/me` on mount to hydrate
 * from an existing session. Exposes `requestMagicLink`, `verifyToken`,
 * `signOut`, `updateProfile`, and the current `buyer`.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BuyerAuthCtx = createContext(null);

const storageKey = (sub) => `entrepren_buyer_session:${sub}`;

export function BuyerAuthProvider({ subdomain, children }) {
  const [buyer, setBuyer]   = useState(null);
  const [ready, setReady]   = useState(false);
  const [config, setConfig] = useState({ enabled: true, welcome_message: '' });

  const token = useMemo(() => (subdomain ? localStorage.getItem(storageKey(subdomain)) : null), [subdomain]);
  const authHeaders = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Hydrate on mount
  useEffect(() => {
    if (!subdomain) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/buyers/${subdomain}/config`);
        if (!cancelled) setConfig(data);
      } catch (_) { /* silent */ }

      const t = localStorage.getItem(storageKey(subdomain));
      if (t) {
        try {
          const { data } = await axios.get(`${API}/public/buyers/${subdomain}/me`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          if (!cancelled) setBuyer(data);
        } catch (_) {
          localStorage.removeItem(storageKey(subdomain));
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [subdomain]);

  const requestMagicLink = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/public/buyers/${subdomain}/magic-link`, { email });
    return data;
  }, [subdomain]);

  const verifyToken = useCallback(async (magicToken) => {
    const { data } = await axios.post(`${API}/public/buyers/${subdomain}/verify`, { token: magicToken });
    localStorage.setItem(storageKey(subdomain), data.session_token);
    setBuyer(data.buyer);
    return data;
  }, [subdomain]);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/public/buyers/${subdomain}/logout`, {}, { headers: authHeaders() });
    } catch (_) { /* silent */ }
    localStorage.removeItem(storageKey(subdomain));
    setBuyer(null);
    toast.success('Signed out');
  }, [subdomain, authHeaders]);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await axios.put(`${API}/public/buyers/${subdomain}/me`, patch, { headers: authHeaders() });
    setBuyer(data);
    return data;
  }, [subdomain, authHeaders]);

  const value = useMemo(() => ({
    subdomain,
    buyer,
    ready,
    config,
    signedIn: !!buyer,
    authHeaders,
    requestMagicLink,
    verifyToken,
    signOut,
    updateProfile,
  }), [subdomain, buyer, ready, config, authHeaders, requestMagicLink, verifyToken, signOut, updateProfile]);

  return <BuyerAuthCtx.Provider value={value}>{children}</BuyerAuthCtx.Provider>;
}

export function useBuyerAuth() {
  const ctx = useContext(BuyerAuthCtx);
  if (!ctx) throw new Error('useBuyerAuth() must be used inside <BuyerAuthProvider>');
  return ctx;
}
